import isEmpty from 'lodash/isEmpty';

import {
    fetchFeedbackFeatures,
    updateFeedbackFeature,
    fetchFeedbackAdminSettings,
    updateFeedbackAdminSetting,
    updateFeedbackTrial,
    setFeedbackAdminSetting,
    fetchFeedbackEmailWhitelabel,
    updateFeedbackEmailWhitelabel,
    toggleFeedbackEmailWhitelabel,
} from './feedback-settings.utils.js';

const getFeatures = async (commit, subName) => {
    try {
        const feedbackFeatures = await fetchFeedbackFeatures(subName);
        commit('setFeaturesMap', { map: Object.freeze(feedbackFeatures) });
    } catch (error) {
        commit('setFeaturesMap', { map: {} });
        commit('setFeatureError', { error });
    }
};

const getSettings = async (commit, subName) => {
    try {
        const feedbackAdminSettings = await fetchFeedbackAdminSettings(subName);
        commit('setAdminSettingsMap', {
            map: Object.freeze(feedbackAdminSettings),
        });
    } catch (adminError) {
        commit('setAdminSettingsMap', {
            map: {},
        });
        commit('setAdminSettingsError', { adminError });
    }
};

const getEmailWhitelabel = async (commit, subName, vendorId) => {
    try {
        const emailWhitelabel = await fetchFeedbackEmailWhitelabel(subName, vendorId);
        commit('setEmailWhitelabelSettings', { map: Object.freeze(emailWhitelabel) });
    } catch (emailWhitelabelError) {
        commit('setEmailWhitelabelSettings', {
            map: {
                whitelabel_settings: {},
                verification_records: {},
                verified: null,
            },
        });
        commit('setEmailWhitelabelError', { emailWhitelabelError });
    }
};

const updateEmailWhitelabelSettings = async (commit, subName, vendorId, value) => {
    try {
        const emailWhitelabel = await updateFeedbackEmailWhitelabel(subName, vendorId, value);
        commit('setEmailWhitelabelSettings', { map: Object.freeze(emailWhitelabel) });
    } catch (emailWhitelabelError) {
        commit('setEmailWhitelabelSettings', {
            map: {
                whitelabel_settings: {},
                verification_records: {},
                verified: null,
            },
        });
        commit('setEmailWhitelabelError', { emailWhitelabelError });
    }
};

const toggleEmailWhitelabelSettings = async (commit, subName, vendorId, value) => {
    try {
        const emailWhitelabel = await toggleFeedbackEmailWhitelabel(subName, vendorId, value);
        commit('setEmailWhitelabelSettings', { map: Object.freeze(emailWhitelabel) });
    } catch (emailWhitelabelError) {
        commit('setEmailWhitelabelSettings', {
            map: {
                whitelabel_settings: {},
                verification_records: {},
                verified: null,
            },
        });
        commit('setEmailWhitelabelError', { emailWhitelabelError });
    }
};

export default {
    namespaced: true,
    state: {
        adminError: null,
        adminSettingsMap: {},
        fetchingFeatures: false,
        featuresMap: {},
        fetchingAdminSettings: false,
        featureError: null,
        updatingFeatureError: null,
        updatingFeature: null,
        updatingSettingError: null,
        updatingSetting: null,
        updatingTrial: null,
        updatingTrialError: null,
        emailWhitelabelSettings: {},
        emailWhitelabelVerificationRecords: {},
        emailWhitelabelVerified: null,
        fetchingEmailWhitelabel: null,
        emailWhitelabelError: null,
        updatingEmailWhitelabel: null,
        updatingEmailWhitelabelError: null,
    },
    mutations: {
        setFeaturesMap (state, { map }) {
            state.featuresMap = map;
        },
        setFeatureFetching (state, { fetchingFeatures }) {
            state.fetchingFeatures = fetchingFeatures;
        },
        setUpdatingFeature (state, { updatingFeature }) {
            state.updatingFeature = updatingFeature;
        },
        setFeatureError (state, { featureError }) {
            state.featureError = featureError;
        },
        setUpdatingFeatureError (state, { updatingFeatureError }) {
            state.updatingFeatureError = updatingFeatureError;
        },
        setAdminSettingsMap (state, { map }) {
            state.adminSettingsMap = map;
        },
        setAdminSettingsFetching (state, { fetchingAdminSettings }) {
            state.fetchingAdminSettings = fetchingAdminSettings;
        },
        setUpdatingSetting (state, { updatingSetting }) {
            state.updatingSetting = updatingSetting;
        },
        setAdminSettingsError (state, { adminError }) {
            state.adminError = adminError;
        },
        setUpdatingSettingError (state, { setting, error }) {
            state.updatingSettingError = { setting, error };
        },
        setUpdatingTrial (state, { updatingTrial }) {
            state.updatingTrial = updatingTrial;
        },
        setUpdatingTrialError (state, { error }) {
            state.updatingTrialError = error;
        },
        setEmailWhitelabelSettings (state, { map }) {
            state.emailWhitelabelSettings = map.whitelabel_settings;
            state.emailWhitelabelVerificationRecords = map.verification_records;
            state.emailWhitelabelVerified = map.verified;
        },
        setEmailWhitelabelError (state, { emailWhitelabelError }) {
            state.emailWhitelabelError = emailWhitelabelError;
        },
        setEmailWhitelabelFetching (state, { fetchingEmailWhitelabel }) {
            state.fetchingEmailWhitelabel = fetchingEmailWhitelabel;
        },
        setUpdatingEmailWhitelabel (state, { updatingEmailWhitelabel }) {
            state.updatingEmailWhitelabel = updatingEmailWhitelabel;
        },
        setUpdatingEmailWhitelabelError (state, { updatingEmailWhitelabelError }) {
            state.updatingEmailWhitelabelError = updatingEmailWhitelabelError;
        },
    },
    actions: {
        async loadAllFeatureSettings ({ state, commit }, { reFetch = false, subName } = {}) {
            if (!isEmpty(state.featuresMap) && !reFetch) {
                return;
            }
            commit('setFeatureFetching', { fetchingFeatures: true });

            await getFeatures(commit, subName);

            commit('setFeatureFetching', { fetchingFeatures: false });
        },
        async loadAllAdminSettings ({ state, commit }, { reFetch = false, subName } = {}) {
            if (!isEmpty(state.adminSettingsMap) && !reFetch) {
                return;
            }
            commit('setAdminSettingsFetching', { fetchingAdminSettings: true });
            await getSettings(commit, subName);
            commit('setAdminSettingsFetching', {
                fetchingAdminSettings: false,
            });
        },
        async updateFeature ({ commit }, { vendorId, featureKey, featureValue, subName }) {
            commit('setUpdatingFeature', { updatingFeature: featureKey });

            try {
                await updateFeedbackFeature(vendorId, featureKey, featureValue, subName);
                await getFeatures(commit, subName);
            } catch (updatingFeatureError) {
                commit('setUpdatingFeatureError', { updatingFeatureError });
            }

            commit('setUpdatingFeature', { updatingFeature: null });
        },
        async updateAdminSetting ({ commit }, { vendorId, setting, subName }) {
            commit('setUpdatingSetting', { updatingSetting: setting });
            try {
                await updateFeedbackAdminSetting(vendorId, setting, subName);
                await getSettings(commit, subName);
            } catch (updatingSettingError) {
                commit('setUpdatingSettingError', { setting, error: updatingSettingError.response.data });
            }
            commit('setUpdatingSetting', { updatingSetting: null });
        },
        async setAdminSetting ({ commit }, { vendorId, setting, subName, value }) {
            commit('setUpdatingSetting', { updatingSetting: setting });
            try {
                await setFeedbackAdminSetting(vendorId, setting, subName, value);
                await getSettings(commit, subName);
            } catch (updatingSettingError) {
                commit('setUpdatingSettingError', { setting, error: updatingSettingError.response.data });
            }
            commit('setUpdatingSetting', { updatingSetting: null });
        },
        async updateTrial ({ commit }, { vendorId, setting, value, subName }) {
            commit('setUpdatingTrial', { updatingTrial: setting });

            try {
                await updateFeedbackTrial(vendorId, value, subName);
                await getFeatures(commit, subName);
            } catch (updatingTrialError) {
                commit('setUpdatingTrialError', { updatingTrialError });
            }
            commit('setUpdatingTrial', { updatingTrial: null });
        },
        async loadEmailWhitelabel ({ state, commit }, { reFetch = false, subName, vendorId } = {}) {
            if (!isEmpty(state.emailWhitelabelSettings) && !reFetch) {
                return;
            }
            commit('setEmailWhitelabelFetching', { fetchingEmailWhitelabel: true });
            await getEmailWhitelabel(commit, subName, vendorId);
            commit('setEmailWhitelabelFetching', { fetchingEmailWhitelabel: false });
        },
        async updateEmailWhitelabel ({ commit }, { vendorId, subName, value }) {
            commit('setUpdatingEmailWhitelabel', { updatingEmailWhitelabel: true });

            try {
                await updateEmailWhitelabelSettings(commit, subName, vendorId, value);
            } catch (updatingEmailWhitelabelError) {
                commit('setUpdatingEmailWhitelabelError', { updatingEmailWhitelabelError });
            }
            commit('setUpdatingEmailWhitelabel', { updatingEmailWhitelabel: null });
        },
        async toggleEmailWhitelabel ({ commit }, { vendorId, subName, value }) {
            commit('setUpdatingEmailWhitelabel', { updatingEmailWhitelabel: true });

            try {
                await toggleEmailWhitelabelSettings(commit, subName, vendorId, value);
            } catch (updatingEmailWhitelabelError) {
                commit('setUpdatingEmailWhitelabelError', { updatingEmailWhitelabelError });
            }
            commit('setUpdatingEmailWhitelabel', { updatingEmailWhitelabel: null });
        },
    },
};
